import { useAuthStore } from '../hooks/useAuth';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { Activity, Truck, ClipboardList, User, CheckCircle, RefreshCw, Mail, Sparkles, MapPin, X, ChevronDown, ChevronRight, Package, ArrowRight, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatTimeInTimeZone, formatDateInTimeZone, getWarehouseLocalComponents } from '../utils/timezoneDisplay';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
} from '../components/ui/alert-dialog';


import { useUIStore } from '../hooks/useUIStore';
import { DelayNotificationSystem } from '../components/DelayNotificationSystem';
import { DelayResolutionModal } from '../components/DelayResolutionModal';


import { CheckInModal } from '../components/dashboard/CheckInModal';
import { LateTimeModal } from '../components/LateTimeModal';
import { useAppointmentActions } from '../hooks/useAppointmentActions';
import { Appointment } from '../types/appointments';
import { ApprovalQueueList } from '../components/appointments/ApprovalQueueList';
import { detectLatePORisk } from '../components/appointments/ApprovalQueueCard';
import AgentSummaryCard from '../components/dashboard/AgentSummaryCard';
import { DwellBadge } from '../components/dashboard/DwellBadge';
import { ErrorBoundary } from '../components/ErrorBoundary';


import { DashboardSection, useCollapsible } from '../components/dashboard/DashboardSection';
import { DockLoadForecast } from '../components/dashboard/DockLoadForecast';

import { DockLoadBalancingPanel } from '../components/dashboard/DockLoadBalancingPanel';
import { CarrierPerformanceAlerts } from '../components/dashboard/CarrierPerformanceAlerts';
import { NextWatchList } from '../components/dashboard/NextWatchList';
import { StackingAlertStrip } from '../components/dashboard/StackingAlertStrip';
import { ProjectedDetentionExposure } from '../components/dashboard/ProjectedDetentionExposure';
import { DashboardSkeleton } from '../components/dashboard/DashboardSkeleton';
import { isAppointmentToday, isActiveOccupant } from '../lib/operational-status';
import { RiskBadge } from '../components/RiskBadge';
import { RecoverySuggestions } from '../components/dashboard/RecoverySuggestions';
import { generateOperatingSlots, DEFAULT_REVISE_SLOTS } from '../utils/generateOperatingSlots';
import { getRankedDockAssignments, type DockAssignmentCandidate, type ActiveBlackout } from '../utils/predictiveDockAssignment';
import type { BlackoutWindow } from '../components/docks/BlackoutList';
import { YardQueuePanel } from '../components/dashboard/YardQueuePanel';

import { DockTimeline } from '../components/dashboard/DockTimeline';
import { BottleneckRadar } from '../components/dashboard/BottleneckRadar';
import { computeYardQueueMetrics } from '../utils/yardState';
import { computeExceptions } from '../utils/exceptionEscalation';
import { computeRecoverySuggestions } from '../utils/recoverySuggestions';
import { computeYardQueue } from '../utils/yardQueue';
import { computeDockLoadBalancing } from '../utils/dockLoadBalancing';
import { computeDetentionExposure, computeBillableHours } from '../utils/detentionExposure';
import { computeMoveImpact } from '../utils/moveImpactPreview';

import { OnboardingChecklist, type OnboardingSignals } from '../components/dashboard/OnboardingChecklist';
import { extractPOMetadataForScoring } from '../utils/poMetadataHelpers';
import { formatDockCapabilities } from '../utils/formatDockCapabilities';
import { extractPOExceptionFromReceiptNotes } from '../utils/extractPOExceptionFromReceiptNotes';
import { formatPOException } from '../utils/formatPOException';
import { DockHealthColumn } from '../components/dashboard/DockHealthColumn';
import { dockMutations } from '../lib/dockMutations';
import { isOperationsTierUser } from '../utils/entitlements';

import './Dashboard.css';


// Feature Flag for Risk Data
const INCLUDE_RISK_DATA = true;

/*
interface Appointment {
    // Local definition removed in favor of shared type
}
*/

interface Dock {
    id: string;
    dockNumber: string;
    type: string;
    status: string;
    // INV-3: Dock capability tags
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
}

// --- Network Helpers ---

function handleUnauthorized(setLocation: (path: string) => void) {
    console.error('Unauthorized access detected. Redirecting to login.');
    localStorage.clear();
    setLocation('/login');
}

async function safeFetchJson(
    url: string,
    options: RequestInit,
    setLocation: (path: string) => void,
    abortSignal?: AbortSignal
): Promise<any> {
    try {
        const response = await fetch(url, { ...options, signal: abortSignal });

        if (response.status === 401) {
            handleUnauthorized(setLocation);
            throw new Error('Unauthorized');
        }

        // Handle 204 No Content
        if (response.status === 204) {
            return null;
        }

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            const errorMessage = data?.error || data?.message || data?.details || `${response.status} ${response.statusText}`;
            const err = new Error(errorMessage);
            (err as any).status = response.status;
            throw err;
        }

        return data;
    } catch (error: any) {
        // If it's an abort error, rethrow it so the caller can handle it (usually ignore)
        if (error.name === 'AbortError') {
            throw error;
        }
        // Otherwise, rethrow the error to be handled by the caller (UI toast, etc)
        throw error;
    }
}


export function Dashboard() {
    const { user, token } = useAuthStore();
    const [, setLocation] = useLocation();
    const searchParams = new URLSearchParams(window.location.search);
    const resolveDelayId = searchParams.get('resolveDelay');

    // Warehouse name for header  lightweight fetch, browser-cached with inner component
    const [warehouseName, setWarehouseName] = useState<string>('Warehouse');
    const [warehouseCity, setWarehouseCity] = useState<string>('');
    const [warehouseTimeZone, setWarehouseTimezone] = useState<string>('');
    const [warehouseBookingId, setWarehouseBookingId] = useState<string>('');
    useEffect(() => {
        if (!user?.warehouseId || !token) return;
        fetch(`/api/warehouses/${user.warehouseId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
                if (d?.warehouse?.name) setWarehouseName(d.warehouse.name);
                if (d?.warehouse?.city) setWarehouseCity(d.warehouse.city);
                if (d?.warehouse?.timeZone) setWarehouseTimezone(d.warehouse.timeZone);
                if (d?.warehouse?.publicBookingId) setWarehouseBookingId(d.warehouse.publicBookingId);
            })
            .catch(() => {});
    }, [user?.warehouseId, token]);

    // Derive short timezone label (e.g. "America/Los_Angeles"  "Pacific Time (PT)")
    const tzShortLabel = (() => {
        if (!warehouseTimeZone) return '';
        try {
            const short = new Intl.DateTimeFormat('en-US', { timeZone: warehouseTimeZone, timeZoneName: 'short' })
                .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
            const long = new Intl.DateTimeFormat('en-US', { timeZone: warehouseTimeZone, timeZoneName: 'long' })
                .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || warehouseTimeZone;
            return short ? `${long} (${short})` : long;
        } catch { return warehouseTimeZone; }
    })();

    const getDashboardContent = () => {
        switch (user?.role) {
            case 'warehouse_manager':
            case 'warehouse_staff':
                return <WarehouseOperationsDashboard />;
            case 'carrier':
                return <CarrierDashboard />;
            case 'admin':
                return <AdminDashboard />;
            default:
                return <div>Unknown role</div>;
        }
    };

    const hasWarehouseAccess = ['warehouse_manager', 'warehouse_staff', 'admin'].includes(user?.role || '');

    return (
        <div className="dashboard-container relative flex flex-col h-screen p-4 sm:p-6 bg-background text-foreground transition-colors duration-300 overflow-hidden mob-header-pt">
            {/* Delay System Integration */}
            {hasWarehouseAccess && <DelayNotificationSystem />}

            {hasWarehouseAccess && resolveDelayId && (
                <DelayResolutionModal
                    appointmentId={resolveDelayId}
                    onClose={() => setLocation('/dashboard')}
                />
            )}

            {/* Header - Clean Inline */}
            {hasWarehouseAccess && (
                <div className="flex items-center justify-between mb-6 sm:mb-8 px-2 pt-2">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-primary/10 rounded-xl border border-primary/20 shadow-sm shrink-0">
                            <Activity className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-base sm:text-xl font-bold text-foreground tracking-tight truncate">
                                {warehouseName}{warehouseCity ? `  ${warehouseCity}` : ''}
                            </h1>
                            <p className="text-xs sm:text-sm text-muted-foreground font-medium flex items-center gap-1.5 flex-wrap">
                                {warehouseBookingId && (
                                    <>
                                        <span className="font-mono text-[10px] text-muted-foreground/80">ID: {warehouseBookingId}</span>
                                        <span className="w-1 h-1 rounded-full bg-slate-400 hidden sm:inline-block"></span>
                                    </>
                                )}
                                {tzShortLabel && (
                                    <>
                                        <span className="text-[10px] text-muted-foreground/70">{tzShortLabel}</span>
                                        <span className="w-1 h-1 rounded-full bg-slate-400 hidden sm:inline-block"></span>
                                    </>
                                )}
                                <span className="hidden sm:inline">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                                <span className="sm:hidden">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-400 hidden sm:inline-block"></span>
                                <span className="uppercase tracking-wider text-[10px]">Live Operations</span>
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                        <UnreadMessagesBadge />
                        <div className="hidden md:flex items-center gap-2 text-xs font-medium text-muted-foreground bg-secondary/50 px-3 py-1.5 rounded-full border border-border">
                            <span>System Status</span>
                            <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                            <span className="text-emerald-600 dark:text-emerald-400">Operational</span>
                        </div>
                    </div>
                </div>
            )}


            <div className="flex-1 overflow-y-auto">
                <ErrorBoundary>
                    {getDashboardContent()}
                </ErrorBoundary>
                {/* Global Modals */}
                {hasWarehouseAccess && <CheckInModal />}
            </div>
        </div>
    );
}


function toTimeMsSafe(iso?: string | null): number | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return isNaN(t) ? null : t;
}


// AI Dock Recommendation Helpers
function normalizeDockStatus(status?: string | null): string {
    return (status || '').trim().toLowerCase();
}

function canonicalizeType(label?: string | null): 'reefer' | 'dry' | 'flatbed' | 'container' | 'other' {
    const lower = (label || '').toLowerCase();
    if (lower.includes('reefer') || lower.includes('refrigerated')) return 'reefer';
    if (lower.includes('dry') || lower.includes('van')) return 'dry';
    if (lower.includes('flat')) return 'flatbed';
    if (lower.includes('container') || lower.includes('intermodal')) return 'container';
    return 'other';
}

function prettyType(canonical: string): string {
    switch (canonical) {
        case 'reefer': return 'Reefer';
        case 'dry': return 'Dry Van';
        case 'flatbed': return 'Flatbed';
        case 'container': return 'Container';
        default: return 'Other';
    }
}

function dockNumberToInt(dockNumber?: string | null): number {
    const parsed = parseInt(dockNumber || '', 10);
    return isNaN(parsed) ? Infinity : parsed;
}

// Expects docks to be pre-filtered for availability
function pickRecommendedDock(appt: Appointment | null, docks: Dock[]): Dock | null {
    if (!appt || docks.length === 0) return null;

    const apptType = canonicalizeType(appt.trailerType);

    const scored = docks.map(dock => {
        const dockType = canonicalizeType(dock.type);
        let score = 0;

        // Exact canonical match (prefer specific types over 'other')
        if (apptType === dockType && apptType !== 'other') {
            score = 100;
        } else if (apptType === 'other' || dockType === 'other') {
            // Soft match if one side is generic
            score = 10;
        } else {
            // Mismatch
            score = 0;
        }

        return { dock, score };
    });

    // Filter out zero scores if we want to be strict, but requirements imply we just pick best.
    // Let's sort by score desc, then dock number asc
    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const numA = dockNumberToInt(a.dock.dockNumber);
        const numB = dockNumberToInt(b.dock.dockNumber);
        if (numA !== numB) return numA - numB;
        return a.dock.id.localeCompare(b.dock.id);
    });

    return scored.length > 0 && scored[0].score > 0 ? scored[0].dock : null;
}


function WarehouseOperationsDashboard() {
    const { user, token, updateUser, logout } = useAuthStore();
    const [, setLocation] = useLocation();

    const [pendingRequests, setPendingRequests] = useState<Appointment[]>([]);
    const [warehouseTimeZone, setWarehouseTimeZone] = useState<string | undefined>(undefined);
    const [todaySchedule, setTodaySchedule] = useState<Appointment[]>([]);
    const [activeOccupants, setActiveOccupants] = useState<Appointment[]>([]); // For KPI calc
    const [docks, setDocks] = useState<Dock[]>([]);
    const [docksError, setDocksError] = useState(false);
    const [activeBlackouts, setActiveBlackouts] = useState<ActiveBlackout[]>([]);
    const [timelineBlackouts, setTimelineBlackouts] = useState<BlackoutWindow[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasRefreshedMe, setHasRefreshedMe] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [selectedRequest, setSelectedRequest] = useState<Appointment | null>(null); // For Pending Request Modal
    const [selectedRequestMode, setSelectedRequestMode] = useState<'view' | 'decline' | 'revise'>('view');
    const [showDockModal, setShowDockModal] = useState(false);
    const [isApproving, setIsApproving] = useState(false);

    const [poCount, setPoCount] = useState<number>(0);
    const [loadCount, setLoadCount] = useState<number>(0);
    const [onboardingSignals, setOnboardingSignals] = useState<OnboardingSignals>({
        hasWarehouse: false, hasDocks: false, hasCustomers: false, hasPOs: false,
        hasLoads: false, hasAppointments: false, hasApproved: false, hasCheckedIn: false,
    });

    // Track when user returns from a checklist-driven task
    const [returnedFromOnboarding, setReturnedFromOnboarding] = useState(() => {
        return new URLSearchParams(window.location.search).get('from') === 'onboarding';
    });

    // Warehouse schedule for revise-time slot generation
    const [warehouseSchedule, setWarehouseSchedule] = useState<{
        name: string; openTime: string; closeTime: string; slotMinutes: number; detentionGraceMinutes: number;
    } | null>(null);

    const prevDocksCountRef = useRef<number | null>(null);
    const prevPoCountRef = useRef<number | null>(null);

    const { openAppointmentDetail, openDockDetail, pendingApprovalAppointment, clearPendingApprovalAppointment, registerActionSuccessListener, unregisterActionSuccessListener } = useUIStore();

    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Refs for exception escalation scroll targets
    const recoverySuggestionsRef = useRef<HTMLDivElement>(null);
    const detentionExposureRef = useRef<HTMLDivElement>(null);

    // ─── Show/Hide flags for empty-panel collapsing ───────────────────
    const now = Date.now();
    const exceptionsResult = useMemo(
        () => computeExceptions(todaySchedule, activeOccupants, docks, now, warehouseTimeZone),
        [todaySchedule, activeOccupants, docks, now],
    );
    const hasExceptions = exceptionsResult.totalCount > 0;
    const hasRecovery = useMemo(
        () => computeRecoverySuggestions(activeOccupants, todaySchedule, docks, now).length > 0,
        [activeOccupants, todaySchedule, docks, now],
    );
    const hasYardQueue = useMemo(
        () => computeYardQueue(todaySchedule, activeOccupants, docks, now, warehouseTimeZone).totalWaiting > 0,
        [todaySchedule, activeOccupants, docks, now, warehouseTimeZone],
    );
    const hasBalancing = useMemo(
        () => computeDockLoadBalancing(todaySchedule, docks, now, 4, warehouseTimeZone).totalOpportunities > 0,
        [todaySchedule, docks, now, warehouseTimeZone],
    );
    // CarrierPerformanceAlerts fetches its own data via SWR, so we rely on its internal empty state.
    // We approximate: if there are any carriers at all, show the panel (it self-hides when no alerts).
    const hasCarrierAlerts = true; // Always show  panel self-collapses with its own empty state
    const detentionData = useMemo(
        () => computeDetentionExposure(activeOccupants, now),
        [activeOccupants, now],
    );
    const hasDetention = detentionData.totalCost > 0 || detentionData.trucksAtRisk > 0;


    // Collapse state for Today's Schedule (in-card toggle)
    const { expanded: scheduleExpanded, toggle: toggleSchedule } = useCollapsible('dash-schedule');

    // Derived State for Guardrails
    const occupiedDockIds = useMemo(() => {
        const ids = activeOccupants
            .map(o => o.dockId)
            .filter((id): id is string => !!id);
        return new Set(ids);
    }, [activeOccupants]);

    const selectableDocks = useMemo(() =>
        docks.filter(d => normalizeDockStatus(d.status) === 'available' && !occupiedDockIds.has(d.id)),
        [docks, occupiedDockIds]);

    // Milestone Toasts
    useEffect(() => {
        if (loading) return; // ignore during initial loading
        if (prevDocksCountRef.current === 0 && docks.length > 0) {
            toast.success("Nice — your first door is live.");
        }
        prevDocksCountRef.current = docks.length;
    }, [docks.length, loading]);

    useEffect(() => {
        if (loading) return; // ignore during initial loading
        if (prevPoCountRef.current === 0 && poCount > 0) {
            toast.success("Purchase orders added. You're ready for inbound loads.");
        }
        prevPoCountRef.current = poCount;
    }, [poCount, loading]);

    // Data fetching  declared before effects so closures are always fresh
    const loadData = useCallback(async (signal?: AbortSignal) => {
        if (!token) return false;

        try {
            setLoading(true);
            setDocksError(false);

            // 0. Refresh Auth Profile
            let refreshedEffectiveTier = user?.effectiveTier;
            try {
                const profileData = await safeFetchJson(
                    '/api/auth/me',
                    { headers: { 'Authorization': `Bearer ${token}` } },
                    setLocation,
                    signal
                );
                if (profileData?.user) {
                    updateUser(profileData.user);
                    refreshedEffectiveTier = profileData.user.effectiveTier ?? refreshedEffectiveTier;
                }
                setHasRefreshedMe(true);
            } catch (err: any) {
                if (err.name === 'AbortError') throw err;
                const msg = err.message || '';
                if (msg === 'Unauthorized' || msg === 'Forbidden' || msg.includes('401') || msg.includes('403')) {
                    void logout({ skipServerRevoke: true, silent: true });
                    setLocation('/login');
                    setHasRefreshedMe(true);
                    return;
                }
                setHasRefreshedMe(true);
            }

            const canIncludeRisk =
                INCLUDE_RISK_DATA &&
                (refreshedEffectiveTier ? refreshedEffectiveTier === 'operations' : isOperationsTierUser(user));

            // 1. Pending Requests (isolated  failure won't block schedule/docks)
            let pendingData: any = { appointments: [] };
            try {
                const pendingUrl = canIncludeRisk
                    ? '/api/appointments/pending?includeRisk=true'
                    : '/api/appointments/pending';
                pendingData = await safeFetchJson(
                    pendingUrl,
                    { headers: { 'Authorization': `Bearer ${token}` } },
                    setLocation,
                    signal
                );
            } catch (err: any) {
                if (err.name === 'AbortError') throw err;
                console.warn('[Dashboard] Pending requests fetch failed:', err.message);
            }

            // 2. Today's Schedule (isolated  failure won't block docks)
            let scheduleData: any = { appointments: [] };
            try {
                const scheduleUrl = canIncludeRisk
                    ? '/api/appointments?includeRisk=true'
                    : '/api/appointments';
                scheduleData = await safeFetchJson(
                    scheduleUrl,
                    { headers: { 'Authorization': `Bearer ${token}` } },
                    setLocation,
                    signal
                );
            } catch (err: any) {
                if (err.name === 'AbortError') throw err;
                console.warn('[Dashboard] Schedule fetch failed:', err.message);
            }

            // 3. Docks (isolated  tracked separately so UI distinguishes fetch-fail from true-empty)
            let docksData: any = { docks: [] };
            if (user?.warehouseId) {
                try {
                    docksData = await safeFetchJson(
                        `/api/warehouses/${user.warehouseId}/docks`,
                        { headers: { 'Authorization': `Bearer ${token}` } },
                        setLocation,
                        signal
                    );
                } catch (err: any) {
                    if (err.name === 'AbortError') throw err;
                    console.warn('[Dashboard] Docks fetch failed:', err.message);
                    setDocksError(true);
                }
            }

            // 3b. V1.3B: Active Blackouts (for dock selector filtering)
            let fetchedBlackouts: ActiveBlackout[] = [];
            let fetchedTimelineBlackouts: BlackoutWindow[] = [];
            if (user?.warehouseId) {
                try {
                    const blackoutData = await safeFetchJson(
                        '/api/docks/blackouts',
                        { headers: { 'Authorization': `Bearer ${token}` } },
                        setLocation,
                        signal
                    );
                    fetchedBlackouts = blackoutData?.blackouts || [];
                    fetchedTimelineBlackouts = blackoutData?.blackouts || [];
                } catch (err: any) {
                    if (err.name === 'AbortError') throw err;
                    console.warn('[Dashboard] Blackouts fetch failed:', err.message);
                }
            }

            // 4. Lightweight PO Count (isolated)
            let fetchedPoCount = 0;
            if (user?.warehouseId) {
                try {
                    const poData = await safeFetchJson(
                        '/api/warehouse-pos',
                        { headers: { 'Authorization': `Bearer ${token}` } },
                        setLocation,
                        signal
                    );
                    fetchedPoCount = Array.isArray(poData) ? poData.length : (poData?.pos?.length || 0);
                } catch (err: any) {
                    if (err.name === 'AbortError') throw err;
                    console.warn('[Dashboard] PO count fetch failed:', err.message);
                }
            }

            // 4b. Lightweight Load Count for onboarding (isolated; Operations / Trial only)
            let fetchedLoadCount = 0;
            if (user?.warehouseId && refreshedEffectiveTier === 'operations') {
                try {
                    const loadsData = await safeFetchJson(
                        '/api/loads?limit=1',
                        { headers: { 'Authorization': `Bearer ${token}` } },
                        setLocation,
                        signal
                    );
                    fetchedLoadCount = loadsData?.total ?? (loadsData?.loads?.length || 0);
                } catch (err: any) {
                    if (err.name === 'AbortError') throw err;
                    console.warn('[Dashboard] Load count fetch failed:', err.message);
                }
            }

            // 4c. Lightweight Customer Count for onboarding (isolated)
            let fetchedCustomerCount = 0;
            if (user?.warehouseId) {
                try {
                    const custData = await safeFetchJson(
                        '/api/customers',
                        { headers: { 'Authorization': `Bearer ${token}` } },
                        setLocation,
                        signal
                    );
                    fetchedCustomerCount = custData?.customers?.length || 0;
                } catch (err: any) {
                    if (err.name === 'AbortError') throw err;
                    console.warn('[Dashboard] Customer count fetch failed:', err.message);
                }
            }

            // --- Process Data ---
            setPendingRequests(pendingData.appointments || []);

            const allAppointments = scheduleData.appointments || [];

            // Diagnostic: log appointment status breakdown (dev only)
            if (import.meta.env.DEV && allAppointments.length > 0) {
                const statusCounts: Record<string, number> = {};
                allAppointments.forEach((a: Appointment) => { statusCounts[a.status] = (statusCounts[a.status] || 0) + 1; });
                console.log('[Schedule] Total:', allAppointments.length, 'Statuses:', JSON.stringify(statusCounts));
            }

            // RULE: "Today" calc uses local time boundaries (00:00 to 23:59 local)
            // Exclude only terminal-reject statuses; everything else appears on the schedule
            const EXCLUDED_STATUSES = new Set(['declined', 'cancelled']);
            const todayAppts = allAppointments.filter((appt: Appointment) =>
                isAppointmentToday(appt) && !EXCLUDED_STATUSES.has(appt.status)
            );
            setTodaySchedule(todayAppts);

            // RULE: Active Occupants = Checked In / In Progress regardless of date (e.g. overnight)
            const occupants = allAppointments.filter((appt: Appointment) => isActiveOccupant(appt));
            setActiveOccupants(occupants);

            setDocks(docksData.docks || []);
            setActiveBlackouts(fetchedBlackouts);
            setTimelineBlackouts(fetchedTimelineBlackouts);
            setPoCount(fetchedPoCount);
            setLoadCount(fetchedLoadCount);

            // Onboarding signals  derived from data already fetched
            const APPROVED_STATUSES = new Set(['approved', 'scheduled', 'checked_in', 'in_progress', 'completed']);
            const CHECKEDIN_STATUSES = new Set(['checked_in', 'in_progress', 'completed']);
            setOnboardingSignals({
                hasWarehouse: !!(user?.warehouseId && user?.isOnboarded !== false),
                hasDocks: (docksData.docks || []).length > 0,
                hasCustomers: fetchedCustomerCount > 0,
                hasPOs: fetchedPoCount > 0,
                hasLoads: fetchedLoadCount > 0,
                hasAppointments: allAppointments.length > 0 || (pendingData.appointments || []).length > 0,
                hasApproved: allAppointments.some((a: Appointment) => APPROVED_STATUSES.has(a.status)),
                hasCheckedIn: allAppointments.some((a: Appointment) => CHECKEDIN_STATUSES.has(a.status)),
            });

            // 5. Warehouse schedule (for revise-time slots)
            if (user?.warehouseId) {
                // Fail-closed: clear prior warehouse context
                setWarehouseSchedule(null);
                setWarehouseTimeZone(undefined);
                try {
                    const whData = await safeFetchJson(
                        `/api/warehouses/${user.warehouseId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } },
                        setLocation,
                        signal
                    );
                    const wh = whData?.warehouse;
                    if (wh?.openTime && wh?.closeTime) {
                        setWarehouseSchedule({
                            name: wh.name || wh.warehouseName || 'Warehouse',
                            openTime: wh.openTime,
                            closeTime: wh.closeTime,
                            slotMinutes: wh.slotMinutes || 60,
                            detentionGraceMinutes: wh.detention_grace_minutes ?? wh.detentionGraceMinutes ?? 60,
                        });
                    }
                    if (wh?.timeZone || wh?.timezone) {
                        setWarehouseTimeZone(wh.timeZone || wh.timezone);
                    }
                } catch {
                    // Non-fatal: revise dropdown will use fallback slots, now fail-closed
                }
            }

            setLastUpdated(new Date());
            return true;

        } catch (error: any) {
            if (error.name === 'AbortError') {
                return false;
            }
            console.error('Failed to load dashboard data:', error);
            return false;
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [token, user?.warehouseId, setLocation]);

    // Issue 1: Watch pendingApprovalAppointment from AppointmentDetailDialog
    useEffect(() => {
        if (pendingApprovalAppointment) {
            // Convert UI store Appointment shape to local Appointment shape
            const appt = {
                ...pendingApprovalAppointment,
                customerPoNumber: pendingApprovalAppointment.po_number,
                carrierName: pendingApprovalAppointment.carrier_name,
                startAt: pendingApprovalAppointment.scheduled_time,
            } as unknown as Appointment;
            clearPendingApprovalAppointment();
            handleApprove(appt);
        }
    }, [pendingApprovalAppointment]);

    // Stable refresh helper  loadData + bump refreshTrigger for LiveYardMap
    const refreshDashboard = useCallback(() => {
        loadData();
        setRefreshTrigger(prev => prev + 1);
    }, [loadData]);

    // Auth check and redirect
    useEffect(() => {
        if (!token && !loading) {
            setLocation('/login');
        }
    }, [token, loading, setLocation]);

    // Register dashboard refresh as an action success listener
    useEffect(() => {
        registerActionSuccessListener(refreshDashboard);
        return () => unregisterActionSuccessListener(refreshDashboard);
    }, [refreshDashboard, registerActionSuccessListener, unregisterActionSuccessListener]);

    // Refresh dashboard data (incl. onboarding signals) when a PO is created from the overlay wizard
    useEffect(() => {
        const handler = () => refreshDashboard();
        window.addEventListener('baywhiz:po-created', handler);
        return () => window.removeEventListener('baywhiz:po-created', handler);
    }, [refreshDashboard]);

    // Strip ?from=onboarding from URL after detecting return, and show progress toast
    useEffect(() => {
        if (!returnedFromOnboarding) return;
        // Clean URL
        const params = new URLSearchParams(window.location.search);
        params.delete('from');
        const clean = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
        window.history.replaceState(null, '', clean);
    }, [returnedFromOnboarding]);

    // Show toast after data is loaded so signal counts are accurate
    useEffect(() => {
        if (!returnedFromOnboarding || loading) return;
        const completed = Object.values(onboardingSignals).filter(Boolean).length;
        const total = Object.keys(onboardingSignals).length;
        if (completed > 0 && completed < total) {
            toast.success(`Step completed! ${completed} of ${total} done.`, { icon: '\u2705', duration: 3000 });
        }
        // Only show once per return
        setReturnedFromOnboarding(false);
    }, [returnedFromOnboarding, loading, onboardingSignals]);

    useEffect(() => {
        let abortController: AbortController | null = null;

        if (token) {
            abortController = new AbortController();
            loadData(abortController.signal);
        }

        return () => {
            if (abortController) abortController.abort();
        };
    }, [loadData]);


    const handleManualRefresh = async () => {
        const success = await loadData();
        if (success) {
            setRefreshTrigger(prev => prev + 1); // Trigger child re-fetches only on success
            toast.success('Dashboard updated');
        } else {
            // If failed (and not aborted), loadData would verify connection
        }
    };

    const handleApprove = (appointment: Appointment) => {
        setSelectedAppointment(appointment);
        setShowDockModal(true);
    };

    // AI Guardrail Helper
    const getDockGuardrailState = (targetDockId: string | undefined): { ok: boolean; reason?: string; dockNumber?: string; statusLabel?: string; suggestedDockNumber?: string } => {
        // A. Missing dockId
        if (!targetDockId) return { ok: false, reason: 'Select a dock to continue' };

        const dock = docks.find(d => d.id === targetDockId);
        // B. Dock not found
        if (!dock) return { ok: false, reason: 'Selected dock not found. Refresh and try again.' };

        const statusLabel = (dock.status || 'unknown').toString().trim();
        const normalizedStatus = statusLabel.toLowerCase();

        // C. Block only maintenance / OOS (aligned with backend source-of-truth)
        if (normalizedStatus === 'maintenance' || normalizedStatus === 'oos'
            || normalizedStatus.includes('out of service') || normalizedStatus.includes('out_of_service')) {
            return {
                ok: false,
                reason: `Dock ${dock.dockNumber} is ${statusLabel}. Choose another dock.`,
                dockNumber: dock.dockNumber,
                statusLabel,
                suggestedDockNumber: pickRecommendedDock(selectedAppointment, selectableDocks)?.dockNumber
            };
        }

        // D. Warn if dock has an active occupant (but allow  backend overlap check is authoritative)
        const activeOccupant = activeOccupants.find(o => (o.dockId || o.dock_id) === targetDockId);
        if (activeOccupant) {
            // Still ok=true  the backend validateDockAvailability is the authoritative gate.
            // Frontend shows a soft warning so the user knows, but does not block.
            return {
                ok: true,
                reason: `Dock ${dock.dockNumber} has an active occupant  backend will verify availability.`,
                dockNumber: dock.dockNumber,
            };
        }

        return { ok: true };
    };

    const confirmApproval = async (dockId: string) => {
        // Prevent double-submit or missing appointment
        if (!selectedAppointment || isApproving) return;

        try {
            setIsApproving(true);

            // GUARDRAIL CHECK
            const guard = getDockGuardrailState(dockId);
            if (!guard.ok) {
                let msg = guard.reason || 'Invalid dock selection';
                // Attach suggestion if available
                if (guard.suggestedDockNumber) {
                    msg += ` Suggested: Dock ${guard.suggestedDockNumber}.`;
                } else if (guard.reason?.includes('occupied') || guard.reason?.includes('not available')) {
                    msg += ' No available docks right now.';
                }
                toast.error(msg);
                return;
            }

            await safeFetchJson(
                `/api/appointments/${selectedAppointment.id}/approve`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ dockId }),
                },
                setLocation
            );

            toast.success('Appointment approved! Email sent to carrier.');
            setShowDockModal(false);
            setSelectedAppointment(null);
            handleManualRefresh();
        } catch (error: any) {
            if (error.status === 409) {
                toast(`Dock conflict: ${error.message}`, { icon: '\u26a0\ufe0f', style: { borderLeft: '4px solid #f59e0b' } });
            } else {
                toast.error(`Failed to approve: ${error.message}`);
            }
        } finally {
            setIsApproving(false);
        }
    };

    const { checkIn, checkOut, dispatchToDock, lateModalProps } = useAppointmentActions({
        onSuccess: handleManualRefresh,
    });

    // Confirmation state for dock board / card actions
    const [pendingDashAction, setPendingDashAction] = useState<{
        type: 'checkin' | 'checkout' | 'dispatch';
        title: string;
        description: string;
        confirmLabel: string;
        confirmClass: string;
        execute: () => void;
    } | null>(null);

    // 4. Pending Priority Queue  sorting handled by ApprovalQueueList

    // Onboarding checklist visibility: show when not fully set up
    const showOnboardingChecklist = !loading && hasRefreshedMe && user && (
        !onboardingSignals.hasWarehouse ||
        !onboardingSignals.hasDocks ||
        !onboardingSignals.hasCustomers ||
        !onboardingSignals.hasPOs ||
        !onboardingSignals.hasLoads ||
        !onboardingSignals.hasAppointments ||
        !onboardingSignals.hasApproved ||
        !onboardingSignals.hasCheckedIn
    );

    // First-run operator detection:
    // Warehouse configured (has docks) but no scheduling activity yet.
    const isFirstRunOperator = !loading && hasRefreshedMe
        && onboardingSignals.hasDocks
        && todaySchedule.length === 0
        && !onboardingSignals.hasAppointments;

    // Critical pre-onboarding guard: warehouse must exist before dashboard loads
    const needsWarehouseSetup = !loading && hasRefreshedMe && user && (
        !user.warehouseId || user.isOnboarded === false
    );

    if (!token || !user) {
        return <DashboardSkeleton />;
    }

    return (
        <div className="warehouse-dashboard text-foreground max-w-[1920px] mx-auto w-full bg-gray-50 dark:bg-transparent">
            {/* Tab Navigation & Controls */}
            <div className="flex justify-between items-center mb-6 border-b border-border">
                <div className="flex items-center">
                    <span className="px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 border-primary text-primary -mb-px">
                        <span className="sm:hidden">Ops</span>
                        <span className="hidden sm:inline">Operations Overview</span>
                    </span>
                </div>

                {/* Refresh Controls */}
                <div className="flex items-center gap-3 pb-2">
                    <span className="text-xs text-muted-foreground hidden md:inline-block">
                        Last updated: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <button
                        onClick={handleManualRefresh}
                        disabled={loading}
                        className="p-1.5 hover:bg-secondary rounded-lg transition-colors border border-transparent hover:border-border disabled:opacity-50 text-muted-foreground hover:text-foreground tap-target"
                        title="Refresh Dashboard"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {loading || !hasRefreshedMe || !user || !token || !docks ? (
                <DashboardSkeleton />
            ) : needsWarehouseSetup ? (
                /* Critical pre-onboarding: warehouse must exist first */
                <div className="flex items-center justify-center min-h-[60vh] animate-fade-in">
                    <div className="max-w-lg w-full">
                        <OnboardingChecklist signals={onboardingSignals} forceExpanded={returnedFromOnboarding} />
                    </div>
                </div>
            ) : (
                <div className="space-y-6 animate-fade-in">

                    {/* Trial Status Banner */}
                    {user?.trialStatus === 'trialing' && user?.trialEndAt && (() => {
                        const daysLeft = Math.max(0, Math.ceil((new Date(user.trialEndAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                        return (
                            <div className="flex items-center justify-between px-4 py-2.5 rounded-lg border"
                                style={{
                                    background: daysLeft <= 5
                                        ? 'linear-gradient(135deg, rgba(239,68,68,0.12), rgba(239,68,68,0.06))'
                                        : 'linear-gradient(135deg, rgba(8,145,178,0.12), rgba(8,145,178,0.06))',
                                    borderColor: daysLeft <= 5 ? 'rgba(239,68,68,0.25)' : 'rgba(8,145,178,0.25)',
                                }}
                            >
                                <span className="text-sm" style={{ color: daysLeft <= 5 ? '#f87171' : '#0e7490' }}>
                                    <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> left in your free trial
                                </span>
                                <span className="text-xs opacity-60" style={{ color: daysLeft <= 5 ? '#f87171' : '#0e7490' }}>
                                    Ends {new Date(user.trialEndAt).toLocaleDateString()}
                                </span>
                            </div>
                        );
                    })()}

                    {/* Onboarding Checklist  below trial banner, above summary strip */}
                    {showOnboardingChecklist && (
                        <OnboardingChecklist signals={onboardingSignals} forceExpanded={returnedFromOnboarding} />
                    )}



                    {/* Part 2: First-run guidance banner */}
                    {isFirstRunOperator && (
                        <div className="rounded-xl border border-teal-200 dark:border-teal-400/20 bg-teal-50 dark:bg-teal-500/10 px-4 py-3 flex items-center gap-3">
                            <div className="p-1.5 bg-teal-500/10 dark:bg-teal-500/20 rounded-lg shrink-0">
                                <Sparkles className="w-4 h-4 text-teal-500 dark:text-teal-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-teal-900 dark:text-teal-300">Start by scheduling your first appointment to see the dock board come alive.</p>
                                <p className="text-xs text-teal-700/70 dark:text-teal-400/60 mt-0.5">Appointments automatically appear on the dock board and calendar.</p>
                            </div>
                            <button
                                onClick={() => setLocation('/appointments')}
                                className="shrink-0 flex items-center gap-1.5 text-xs font-bold bg-primary hover:bg-primary/90 text-white px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                            >
                                Create appointment
                                <ArrowRight className="w-3 h-3" />
                            </button>
                        </div>
                    )}


                    {/* ═══════════════════════════════════════════════════════
                        APPROVAL QUEUE  shared decision surface
                    ═══════════════════════════════════════════════════════ */}
                    <ApprovalQueueList
                        id="dashboard-approval-queue"
                        requests={pendingRequests}
                        warehouseTimeZone={warehouseTimeZone}
                        onCardClick={(req) => { setSelectedRequest(req); setSelectedRequestMode('view'); }}
                        onApprove={(req) => handleApprove(req)}
                        onRevise={(req) => { setSelectedRequest(req); setSelectedRequestMode('revise'); }}
                        onDecline={(req) => { setSelectedRequest(req); setSelectedRequestMode('decline'); }}
                    />

                    {/* AI Dock Coordinator Summary */}
                    <AgentSummaryCard />

                    {/* ═══════════════════════════════════════════════════════
                        LIVE RIGHT NOW  lightweight status line
                    ═══════════════════════════════════════════════════════ */}
                    {(() => {
                        const atDockCount = todaySchedule.filter(a => a.status === 'checked_in' || a.status === 'in_progress').length;
                        const nowMs = Date.now();
                        const soonWindowMs = 90 * 60 * 1000;
                        const arrivingSoonCount = todaySchedule.filter(a => {
                            if (a.status !== 'approved') return false;
                            const startMs = new Date(a.startAt).getTime();
                            return !isNaN(startMs) && startMs > nowMs && startMs <= nowMs + soonWindowMs;
                        }).length;
                        const waitingCount = todaySchedule.filter(a => a.status === 'approved').length;
                        return (
                            <div className="flex items-center gap-2 px-1">
                                <span className="relative flex h-2 w-2 shrink-0">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                </span>
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Live right now</span>
                                <span className="text-xs text-muted-foreground">
                                    {atDockCount} truck{atDockCount !== 1 ? 's' : ''} at dock
                                    {' • '}
                                    {arrivingSoonCount} arriving soon
                                    {' • '}
                                    {waitingCount} waiting
                                </span>
                            </div>
                        );
                    })()}

                    {/* DOCK HEALTH  global warehouse status strip */}
                    {docks.length > 0 && (
                        <DockHealthColumn
                            docks={docks as any}
                            activeOccupants={activeOccupants as any}
                            scheduledAppointments={todaySchedule as any}
                            onDockClick={() => setLocation('/docks')}
                            onStatusChange={async (dockId, newStatus) => {
                                try {
                                    await dockMutations.updateDockStatus(dockId, newStatus, docks as any);
                                    refreshDashboard();
                                } catch {}
                            }}
                        />
                    )}


                    {/* ═══════════════════════════════════════════════════════
                        TODAY'S SCHEDULE
                    ═══════════════════════════════════════════════════════ */}
                    <DashboardSection label="Today's Schedule" updatedAt={lastUpdated}>
                        <div id="dashboard-todays-schedule" className="bg-gray-100 dark:bg-slate-800/30 border border-border rounded-xl p-4">

                            {scheduleExpanded && (todaySchedule.length === 0 ? (
                                <div className="space-y-3">
                                    <p className="text-muted-foreground text-center py-4 text-sm">No appointments scheduled for today. <button onClick={() => setLocation('/appointments')} className="text-primary hover:underline font-medium">Check the Schedule page</button> for upcoming appointments.</p>
                                </div>
                            ) : (() => {
                                const ts = (v?: string | null) => v ? new Date(v).getTime() : 0;
                                const nowMs = Date.now();

                                const upcomingLane = todaySchedule
                                    .filter(a => ['approved', 'scheduled'].includes(a.status))
                                    .sort((a, b) => ts(a.startAt) - ts(b.startAt));

                                const arrivedLane = todaySchedule
                                    .filter(a => a.status === 'checked_in')
                                    .sort((a, b) => (ts(a.checkInTime) || ts(a.startAt)) - (ts(b.checkInTime) || ts(b.startAt)));

                                const atDockLane = (() => {
                                    const todayAtDock = todaySchedule
                                        .filter(a => a.status === 'in_progress');
                                    const todayIds = new Set(todaySchedule.map(a => (a as any)._id));
                                    const overnightActive = activeOccupants.filter(a => !todayIds.has((a as any)._id));
                                    return [...todayAtDock, ...overnightActive]
                                        .sort((a, b) => (ts(a.dockStartTime) || ts(a.checkInTime) || ts(a.startAt)) - (ts(b.dockStartTime) || ts(b.checkInTime) || ts(b.startAt)));
                                })();

                                const completedLane = todaySchedule
                                    .filter(a => ['completed', 'cancelled'].includes(a.status))
                                    .sort((a, b) =>
                                        (ts(b.checkOutTime) || ts(b.endAt) || ts(b.startAt)) -
                                        (ts(a.checkOutTime) || ts(a.endAt) || ts(a.startAt))
                                    );

                                const lanes: {
                                    key: string; label: string; items: Appointment[];
                                    accent: string; badge: string; priority?: boolean;
                                }[] = [
                                    { key: 'upcoming', label: 'Upcoming', items: upcomingLane, accent: 'border-blue-400 dark:border-blue-600', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
                                    { key: 'arrived', label: 'Arrived', items: arrivedLane, accent: 'border-amber-400 dark:border-amber-500', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', priority: true },
                                    { key: 'completed', label: 'Completed', items: completedLane, accent: 'border-emerald-400 dark:border-emerald-600', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
                                ];

                                // At Dock lane config (rendered separately in grouped column)
                                const atDockLaneConfig = { key: 'atdock', label: 'At Dock', items: atDockLane, accent: 'border-teal-400 dark:border-teal-500', badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' };

                                const handleDockStatusChange = async (dockId: string, newStatus: string) => {
                                    try {
                                        await dockMutations.updateDockStatus(dockId, newStatus, docks as any);
                                        refreshDashboard();
                                    } catch { /* toast handled by dockMutations */ }
                                };

                                const renderLaneCards = (lane: typeof lanes[0]) => (
                                    lane.items.length === 0 ? (
                                        <p className={`text-xs text-muted-foreground/60 text-center ${lane.key === 'completed' ? 'py-1' : 'py-2'}`}>{lane.key === 'completed' ? 'No completions yet' : 'None yet'}</p>
                                    ) : lane.key === 'upcoming' ? (() => {
                                        const overdueItems = lane.items.filter(a => {
                                            const st = new Date(a.startAt).getTime();
                                            return !isNaN(st) && st < nowMs;
                                        });
                                        const futureItems = lane.items.filter(a => {
                                            const st = new Date(a.startAt).getTime();
                                            return isNaN(st) || st >= nowMs;
                                        });
                                        const renderCard = (appt: Appointment) => (
                                            <PipelineCard
                                                key={appt.id}
                                                appointment={appt}
                                                dock={docks.find(d => d.id === appt.dockId)}
                                                laneKey={lane.key}
                                                nowMs={nowMs}
                                                detentionGraceMinutes={warehouseSchedule?.detentionGraceMinutes ?? 60}
                                                warehouseTimeZone={warehouseTimeZone}
                                                onClick={() => openAppointmentDetail({
                                                    ...appt,
                                                    po_number: appt.customerPoNumber,
                                                    carrier_name: appt.carrierName,
                                                    scheduled_time: appt.startAt
                                                })}
                                                onCheckIn={() => setPendingDashAction({
                                                    type: 'checkin',
                                                    title: 'Confirm Check-In',
                                                    description: 'This will start the dwell clock and mark the driver as arrived.',
                                                    confirmLabel: 'Check In',
                                                    confirmClass: 'bg-blue-600 hover:bg-blue-500 text-white',
                                                    execute: () => checkIn(appt),
                                                })}
                                                onDispatch={() => setPendingDashAction({
                                                    type: 'dispatch',
                                                    title: 'Confirm Dispatch to Dock',
                                                    description: 'This will mark the truck as actively occupying the selected dock.',
                                                    confirmLabel: 'Dispatch',
                                                    confirmClass: 'bg-emerald-600 hover:bg-emerald-500 text-white',
                                                    execute: () => dispatchToDock(appt),
                                                })}
                                                onCheckOut={() => setPendingDashAction({
                                                    type: 'checkout',
                                                    title: 'Confirm Completion',
                                                    description: 'This will complete the appointment and release its dock. Eligible linked records may be finalized automatically.',
                                                    confirmLabel: 'Complete',
                                                    confirmClass: 'bg-teal-600 hover:bg-teal-500 text-white',
                                                    execute: () => checkOut(appt),
                                                })}
                                            />
                                        );
                                        return (
                                            <>
                                                {overdueItems.map(renderCard)}
                                                {futureItems.map(renderCard)}
                                            </>
                                        );
                                    })() : (
                                        lane.items.map(appt => (
                                            <PipelineCard
                                                key={appt.id}
                                                appointment={appt}
                                                dock={docks.find(d => d.id === appt.dockId)}
                                                laneKey={lane.key}
                                                nowMs={nowMs}
                                                detentionGraceMinutes={warehouseSchedule?.detentionGraceMinutes ?? 60}
                                                warehouseTimeZone={warehouseTimeZone}
                                                onClick={() => openAppointmentDetail({
                                                    ...appt,
                                                    po_number: appt.customerPoNumber,
                                                    carrier_name: appt.carrierName,
                                                    scheduled_time: appt.startAt
                                                })}
                                                onCheckIn={() => setPendingDashAction({
                                                    type: 'checkin',
                                                    title: 'Confirm Check-In',
                                                    description: 'This will start the dwell clock and mark the driver as arrived.',
                                                    confirmLabel: 'Check In',
                                                    confirmClass: 'bg-blue-600 hover:bg-blue-500 text-white',
                                                    execute: () => checkIn(appt),
                                                })}
                                                onDispatch={() => setPendingDashAction({
                                                    type: 'dispatch',
                                                    title: 'Confirm Dispatch to Dock',
                                                    description: 'This will mark the truck as actively occupying the selected dock.',
                                                    confirmLabel: 'Dispatch',
                                                    confirmClass: 'bg-emerald-600 hover:bg-emerald-500 text-white',
                                                    execute: () => dispatchToDock(appt),
                                                })}
                                                onCheckOut={() => setPendingDashAction({
                                                    type: 'checkout',
                                                    title: 'Confirm Completion',
                                                    description: 'This will complete the appointment and release its dock. Eligible linked records may be finalized automatically.',
                                                    confirmLabel: 'Complete',
                                                    confirmClass: 'bg-teal-600 hover:bg-teal-500 text-white',
                                                    execute: () => checkOut(appt),
                                                })}
                                            />
                                        ))
                                    )
                                );

                                return (
                                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                                        {/* Render Upcoming + Arrived lanes */}
                                        {lanes.filter(l => l.key !== 'completed').map(lane => (
                                            <div
                                                key={lane.key}
                                                className={`rounded-lg border-t-2 ${lane.accent} ${lane.priority ? 'bg-amber-50/60 dark:bg-amber-900/10' : 'bg-white/60 dark:bg-slate-800/40'} border border-slate-200 dark:border-slate-700 flex flex-col ${lane.priority ? 'ring-1 ring-amber-300/30 dark:ring-amber-500/15' : ''}`}
                                            >
                                                <div className={`flex items-center justify-between px-3 py-2 border-b ${lane.priority ? 'border-amber-200 dark:border-amber-700/40' : 'border-slate-200 dark:border-slate-700'}`}>
                                                    <div>
                                                        <span className={`text-xs font-semibold uppercase tracking-wide ${lane.priority ? 'text-amber-700 dark:text-amber-300' : 'text-slate-600 dark:text-slate-300'}`}>
                                                            {lane.label}
                                                        </span>
                                                    </div>
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${lane.badge}`}>
                                                        {lane.items.length}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col gap-2 p-2 flex-1">
                                                    {renderLaneCards(lane)}
                                                </div>
                                            </div>
                                        ))}

                                        {/* At Dock lane  standalone (Dock Health moved to global strip above) */}
                                        <div className={`rounded-lg border-t-2 ${atDockLaneConfig.accent} bg-white/60 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col`}>
                                            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                                                <div>
                                                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                                                        {atDockLaneConfig.label}
                                                    </span>
                                                </div>
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${atDockLaneConfig.badge}`}>
                                                    {atDockLaneConfig.items.length}
                                                </span>
                                            </div>
                                            <div className="flex flex-col gap-2 p-2 flex-1">
                                                {renderLaneCards(atDockLaneConfig)}
                                            </div>
                                        </div>

                                        {/* Completed lane */}
                                        {lanes.filter(l => l.key === 'completed').map(lane => (
                                            <div
                                                key={lane.key}
                                                className={`rounded-lg border-t-2 ${lane.accent} bg-white/60 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 flex flex-col`}
                                            >
                                                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                                                    <div>
                                                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                                                            {lane.label}
                                                        </span>
                                                    </div>
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${lane.badge}`}>
                                                        {lane.items.length}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col gap-2 p-2 flex-1">
                                                    {renderLaneCards(lane)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })())}
                        </div>
                    </DashboardSection>


                    {/* ═══════════════════════════════════════════════════════
                        BAND 4  (Next 3 Hours + Yard Flow removed for layout clarity)
                    ═══════════════════════════════════════════════════════ */}




                    {/* ═══════════════════════════════════════════════════════
                        BAND 5  ADVANCED INSIGHTS (collapsed by default)
                        Recovery, Yard Queue, analytics, trend data, exposure.
                    ═══════════════════════════════════════════════════════ */}
                    {/* Advanced Insights temporarily hidden from Operations Overview */}
                    {false && (
                    <DashboardSection
                        label="Advanced Insights"
                        collapsible
                        storageKey="dash-advanced-insights"
                        defaultExpanded={false}
                        updatedAt={lastUpdated}
                    >
                        {/* Recovery + Yard Queue (moved from default view) */}
                        {hasRecovery && (
                            <div ref={recoverySuggestionsRef}>
                                <RecoverySuggestions warehouseTimeZone={warehouseTimeZone}
                                    activeOccupants={activeOccupants}
                                    todaySchedule={todaySchedule}
                                    docks={docks}
                                    onOpenAppointment={(appt) => openAppointmentDetail({
                                        ...appt,
                                        po_number: appt.customerPoNumber,
                                        carrier_name: appt.carrierName,
                                        scheduled_time: appt.startAt,
                                    })}
                                />
                            </div>
                        )}

                        {hasYardQueue && (
                            <YardQueuePanel
                                todaySchedule={todaySchedule}
                                activeOccupants={activeOccupants}
                                docks={docks}
                                onOpenAppointment={(appt) => openAppointmentDetail({
                                    ...appt,
                                    po_number: appt.customerPoNumber,
                                    carrier_name: appt.carrierName,
                                    scheduled_time: appt.startAt,
                                })}
                                onOpenDock={(dockId) => openDockDetail(dockId)}
                            />
                        )}
                        <DockLoadForecast
                            todaySchedule={todaySchedule}
                            docks={docks}
                        />

                        {hasBalancing && (
                            <DockLoadBalancingPanel
                                todaySchedule={todaySchedule}
                                docks={docks}
                                onOpenAppointment={(appt) => openAppointmentDetail({
                                    ...appt,
                                    po_number: appt.customerPoNumber,
                                    carrier_name: appt.carrierName,
                                    scheduled_time: appt.startAt,
                                })}
                                onOpenDock={(dockId) => openDockDetail(dockId)}
                            />
                        )}

                        {hasCarrierAlerts && (
                            <CarrierPerformanceAlerts
                                onOpenCarrier={(carrierId) => setLocation(`/carriers?highlight=${carrierId}`)}
                            />
                        )}

                        <StackingAlertStrip todaySchedule={todaySchedule} warehouseTimeZone={warehouseTimeZone} />

                        <DockTimeline
                            todaySchedule={todaySchedule}
                            activeOccupants={activeOccupants}
                            docks={docks}
                            blackouts={timelineBlackouts}
                        />

                        <BottleneckRadar
                            todaySchedule={todaySchedule}
                            activeOccupants={activeOccupants}
                            docks={docks}
                        />

                        <div id="dashboard-detention-exposure" ref={detentionExposureRef}>
                            <ProjectedDetentionExposure activeOccupants={activeOccupants} docks={docks} />
                        </div>

                        {/* Completed Loads Summary */}
                        {(() => {
                            const completedToday = todaySchedule.filter(a => a.status === 'completed').length;
                            const cancelledToday = todaySchedule.filter(a => a.status === 'cancelled').length;
                            return (
                                <div className="rounded-xl border border-border bg-card/50 px-5 py-4 flex items-center gap-6">
                                    <div>
                                        <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide mb-0.5">Completed</div>
                                        <div className="text-2xl font-bold text-emerald-500 tabular-nums">{completedToday}</div>
                                    </div>
                                    {cancelledToday > 0 && (
                                        <div>
                                            <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide mb-0.5">Cancelled</div>
                                            <div className="text-2xl font-bold text-red-400 tabular-nums">{cancelledToday}</div>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide mb-0.5">Total Scheduled</div>
                                        <div className="text-2xl font-bold text-foreground tabular-nums">{todaySchedule.length}</div>
                                    </div>
                                </div>
                            );
                        })()}
                    </DashboardSection>
                    )}

                    {/* Dock Selection Modal */}
                    {
                        showDockModal && selectedAppointment && (
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000]" onClick={() => {
                                if (!isApproving) {
                                    setShowDockModal(false);
                                    setSelectedAppointment(null);
                                }
                            }}>
                                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl p-6 max-w-lg w-full m-4" onClick={(e) => e.stopPropagation()}>
                                    <h3 className="text-xl font-bold mb-1">Assign Dock</h3>
                                    <p className="text-slate-500 text-sm mb-5">
                                        Select a dock for <span className="font-semibold text-slate-900 dark:text-white">{selectedAppointment.driverName || selectedAppointment.carrierName}</span>
                                        {selectedAppointment.trailerType && (
                                            <span className="ml-1 text-xs text-slate-400"> {selectedAppointment.trailerType}</span>
                                        )}
                                    </p>

                                    <div className="space-y-2 mb-5 max-h-[400px] overflow-y-auto custom-scrollbar pr-1">
                                        {(() => {
                                            // INV-4.1: Use shared aggregation helper
                                            const poMeta = extractPOMetadataForScoring(selectedAppointment?.linkedPOs);
                                            const ranked = getRankedDockAssignments(
                                                selectedAppointment,
                                                docks,
                                                todaySchedule,
                                                activeOccupants,
                                                Date.now(),
                                                warehouseTimeZone,
                                                poMeta,
                                                activeBlackouts
                                            );

                                            if (ranked.filter(c => c.isEligible).length === 0 && docks.length > 0) {
                                                return (
                                                    <p className="text-amber-500 text-sm p-4 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800 mb-4">
                                                        No available docks. Check operating map or wait for a dock to clear.
                                                    </p>
                                                );
                                            }

                                            return ranked.map((candidate: DockAssignmentCandidate, idx: number) => {
                                                const isBestMatch = candidate.isRecommended;

                                                if (!candidate.isEligible) {
                                                    // Ineligible dock  show disabled with conflict window
                                                    return (
                                                        <button
                                                            key={candidate.dockId}
                                                            disabled
                                                            className="w-full p-3 text-left border rounded-lg bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800 opacity-50 cursor-not-allowed"
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <div>
                                                                    <div className="font-semibold text-slate-400">Dock {candidate.dockNumber}</div>
                                                                    <div className="text-xs text-slate-400">
                                                                        {(() => { const cap = formatDockCapabilities(docks.find(d => d.id === candidate.dockId) || {}); return cap ? `${cap}  ${candidate.dockType}` : candidate.dockType; })()}
                                                                    </div>
                                                                    {candidate.overlapWindow && (
                                                                        <div className="text-[10px] text-red-400 mt-0.5">⚠ Occupied {candidate.overlapWindow}</div>
                                                                    )}
                                                                </div>
                                                                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 uppercase">
                                                                    {candidate.ineligibleReason}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    );
                                                }

                                                // Eligible dock  show with score reasons
                                                return (
                                                    <div key={candidate.dockId}>
                                                        {/* Best Match separator */}
                                                        {isBestMatch && (
                                                            <div className="mb-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <div className="flex items-center gap-1.5 text-indigo-700 dark:text-indigo-300 font-bold text-sm">
                                                                        <Sparkles className="w-4 h-4 text-indigo-500 fill-indigo-500/20" />
                                                                        Best Match  Dock {candidate.dockNumber}
                                                                    </div>
                                                                    {/* INV-5: Capability summary */}
                                                                    {(() => { const cap = formatDockCapabilities(docks.find(d => d.id === candidate.dockId) || {}); return cap ? <span className="text-[10px] text-muted-foreground font-normal ml-1">({cap})</span> : null; })()}
                                                                    <button
                                                                        onClick={() => confirmApproval(candidate.dockId)}
                                                                        disabled={isApproving}
                                                                        className="btn btn-xs bg-indigo-600 hover:bg-indigo-700 text-white border-none shadow-sm shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        {isApproving ? 'Assigning...' : 'Assign'}
                                                                    </button>
                                                                </div>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {candidate.reasons.slice(0, 3).map((r, ri) => (
                                                                        <span
                                                                            key={ri}
                                                                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.positive
                                                                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                                                }`}
                                                                        >
                                                                            {r.label}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Alternative header after best match */}
                                                        {idx === 1 && ranked[0]?.isRecommended && (
                                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 mt-3">
                                                                Alternatives
                                                            </div>
                                                        )}

                                                        {/* Regular dock row (skip for best match  already rendered above) */}
                                                        {!isBestMatch && (() => {
                                                            // FIX 2: Deduped schedule includes activeOccupants so overnight / spillover loads are visible
                                                            const dedupedSchedule = (() => {
                                                                const seen = new Set(todaySchedule.map(a => a.id));
                                                                return [...todaySchedule, ...activeOccupants.filter(a => !seen.has(a.id))];
                                                            })();
                                                            const impact = selectedAppointment ? computeMoveImpact(selectedAppointment, candidate.dockId, dedupedSchedule) : null;
                                                            return (
                                                                <button
                                                                    onClick={() => confirmApproval(candidate.dockId)}
                                                                    disabled={isApproving}
                                                                    className="w-full p-3 text-left border rounded-lg transition-all bg-slate-100 dark:bg-slate-800 border-transparent hover:bg-primary/5 hover:border-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <div>
                                                                            <div className="font-semibold text-foreground">
                                                                                Dock {candidate.dockNumber}
                                                                                {(() => { const cap = formatDockCapabilities(docks.find(d => d.id === candidate.dockId) || {}); return cap ? <span className="text-xs text-muted-foreground font-normal ml-1.5">{cap}</span> : null; })()}
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                {candidate.reasons.slice(0, 3).map((r, ri) => (
                                                                                    <span
                                                                                        key={ri}
                                                                                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${r.positive
                                                                                            ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                                                                            : 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
                                                                                            }`}
                                                                                    >
                                                                                        {r.label}
                                                                                    </span>
                                                                                ))}
                                                                                {impact && !impact.createsConflict && impact.summary !== 'No impact change' && (
                                                                                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20">
                                                                                        {impact.summary}
                                                                                    </span>
                                                                                )}
                                                                                {candidate.overlapWindow && (
                                                                                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">
                                                                                        ⚠ {candidate.overlapWindow}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <span className="text-xs text-slate-400 font-mono">{candidate.dockType}</span>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })()}
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                    <div className="mt-4 flex justify-end">
                                        <button
                                            onClick={() => {
                                                setShowDockModal(false);
                                                setSelectedAppointment(null);
                                            }}
                                            className="btn btn-ghost btn-sm"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {/* Pending Request Detail Modal */}
                    {
                        selectedRequest && (
                            <RequestDetailModal
                                request={selectedRequest}
                                onClose={() => { setSelectedRequest(null); setSelectedRequestMode('view'); }}
                                onApprove={() => {
                                    handleApprove(selectedRequest);
                                    setSelectedRequest(null);
                                    setSelectedRequestMode('view');
                                }}
                                onRefresh={handleManualRefresh}
                                warehouseSchedule={warehouseSchedule}
                                warehouseTimeZone={warehouseTimeZone}
                                initialMode={selectedRequestMode}
                            />
                        )
                    }
                </div >
            )
            }

            {/* Late check-in/checkout override modal */}
            <LateTimeModal {...lateModalProps} warehouseTimeZone={warehouseTimeZone} />

            {/* Confirmation AlertDialog for dock board / card actions */}
            <AlertDialog open={!!pendingDashAction} onOpenChange={(open) => { if (!open) setPendingDashAction(null); }}>
                <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-slate-900 dark:text-slate-100">{pendingDashAction?.title}</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-600 dark:text-slate-400">{pendingDashAction?.description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className={pendingDashAction?.confirmClass || 'bg-blue-600 text-white'}
                            onClick={() => {
                                pendingDashAction?.execute();
                                setPendingDashAction(null);
                            }}
                        >
                            {pendingDashAction?.confirmLabel}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div >
    );
}

// ─── Relative Time Helper ───────────────────────────────────────────
function relativeTimeLabel(isoOrMs: string | number | null | undefined, nowMs: number, mode: 'future' | 'elapsed'): string {
    if (!isoOrMs) return '--';
    const ms = typeof isoOrMs === 'number' ? isoOrMs : new Date(isoOrMs).getTime();
    if (isNaN(ms)) return '--';
    const diffMs = mode === 'future' ? ms - nowMs : nowMs - ms;
    if (diffMs < 0 && mode === 'future') {
        // overdue
        const overdue = Math.abs(diffMs);
        const mins = Math.round(overdue / 60000);
        if (mins < 60) return `${mins}m overdue`;
        const hrs = Math.floor(mins / 60);
        return `${hrs}h ${mins % 60}m overdue`;
    }
    const totalMins = Math.max(0, Math.round(diffMs / 60000));
    if (totalMins < 1) return 'Now';
    if (totalMins < 60) return `${totalMins}m`;
    const hrs = Math.floor(totalMins / 60);
    const rem = totalMins % 60;
    return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

// ─── Severity Helpers ───────────────────────────────────────────────
function waitSeverity(mins: number): { tier: 0 | 1 | 2 | 3; label: string; textColor: string; borderClass: string; bgClass: string } {
    if (mins >= 45) return { tier: 3, label: 'LONG WAIT', textColor: 'text-red-600 dark:text-red-400', borderClass: 'border-l-4 border-l-red-500 border-red-400/60 dark:border-red-500/50', bgClass: 'bg-red-50/50 dark:bg-red-900/15' };
    if (mins >= 25) return { tier: 2, label: '', textColor: 'text-amber-600 dark:text-amber-400', borderClass: 'border-l-[3px] border-l-amber-500 border-amber-400/60 dark:border-amber-500/40', bgClass: 'bg-amber-50/50 dark:bg-amber-900/15' };
    if (mins >= 10) return { tier: 1, label: '', textColor: 'text-amber-500 dark:text-amber-400', borderClass: 'border-amber-300 dark:border-amber-600/30', bgClass: 'bg-amber-50/30 dark:bg-amber-900/5' };
    return { tier: 0, label: '', textColor: 'text-foreground', borderClass: 'border-border', bgClass: 'bg-card' };
}

function dockSeverity(dockMins: number, graceMinutes: number): { tier: 0 | 1 | 2 | 3; textColor: string; borderClass: string; bgClass: string } {
    // Severity tiers relative to warehouse detention grace window
    const overGrace = dockMins - graceMinutes;
    if (overGrace >= 60) return { tier: 3, textColor: 'text-red-600 dark:text-red-400', borderClass: 'border-l-4 border-l-red-500 border-red-400/60 dark:border-red-500/50', bgClass: 'bg-red-50/40 dark:bg-red-900/15' };
    if (overGrace >= 15) return { tier: 2, textColor: 'text-amber-600 dark:text-amber-400', borderClass: 'border-l-[3px] border-l-amber-500 border-amber-300 dark:border-amber-500/30', bgClass: 'bg-amber-50/30 dark:bg-amber-900/10' };
    if (overGrace >= 0) return { tier: 1, textColor: 'text-amber-500 dark:text-amber-400', borderClass: 'border-amber-300 dark:border-amber-500/20', bgClass: 'bg-amber-50/20 dark:bg-amber-900/5' };
    return { tier: 0, textColor: 'text-muted-foreground', borderClass: 'border-border', bgClass: 'bg-card' };
}

// ─── Pipeline Card (lane-aware) ─────────────────────────────────────
export function PipelineCard({
    appointment,
    dock,
    laneKey,
    nowMs,
    detentionGraceMinutes,
    onClick,
    onCheckIn,
    onDispatch,
    onCheckOut,
    warehouseTimeZone,
}: {
    appointment: Appointment;
    dock?: Dock;
    laneKey: string;
    nowMs: number;
    detentionGraceMinutes: number;
    onClick: () => void;
    onCheckIn: () => void;
    onDispatch: () => void;
    onCheckOut: () => void;
    warehouseTimeZone?: string;
}) {
    if (!appointment) return null;

    const hasTruthydockId = !!(appointment.dockId || appointment.dock_id);
    const hasActiveDock = !!dock?.id;
    const isStaleDock = hasTruthydockId && !hasActiveDock;
    const canDispatch = hasTruthydockId && hasActiveDock;
    
    const dockLabel = dock?.dockNumber ? `Dock ${dock.dockNumber}` : null;
    // Section 1: Never render "MC null"  safe carrier fallback
    const carrierLabel = appointment.carrierName
        || (appointment.carrierMcNumber ? `MC ${appointment.carrierMcNumber}` : 'Carrier unknown');

    // Load / PO display logic:
    //  1. Has load number → "Load: {loadNumber}" (optionally with PO)
    //  2. No load, has single PO → "PO: {poNumber}"
    //  3. No load, has multiple POs → "POs: {first} +N more"
    //  4. No load, no POs → hide line entirely
    const loadPoLine: { label: string; value: string } | null = (() => {
        const load = appointment.loadNumber || '';
        const po = appointment.customerPoNumber || '';
        if (load && po) return { label: 'Load', value: `${load} : ${po}` };
        if (load) return { label: 'Load', value: load };
        // No load — fall through to linked POs
        const linked = appointment.linkedPOs;
        if (linked && linked.length > 1) {
            return { label: 'POs', value: `${linked[0].poNumber} +${linked.length - 1} more` };
        }
        if (linked && linked.length === 1) {
            return { label: 'PO', value: linked[0].poNumber };
        }
        // Single legacy PO field
        if (po) return { label: 'PO', value: po };
        return null; // hide entirely
    })();

    // ── UPCOMING ────────────────────────────────────────────────────
    if (laneKey === 'upcoming') {
        const arrivalRel = relativeTimeLabel(appointment.startAt, nowMs, 'future');
        const isOverdue = (() => {
            if (!appointment.startAt) return false;
            const ms = new Date(appointment.startAt).getTime();
            return !isNaN(ms) && ms < nowMs;
        })();
        return (
            <div
                onClick={onClick}
                className={`rounded-lg border bg-card hover:border-blue-400/50 dark:hover:border-blue-500/40 transition-all cursor-pointer p-4 flex flex-col gap-1.5 min-h-[120px] group ${
                    isOverdue
                        ? 'border-l-[3px] border-l-red-500 border-red-300 dark:border-red-500/40'
                        : 'border-border'
                }`}
            >
                {/* Row 1: Carrier */}
                <div className="text-sm font-semibold leading-snug text-foreground truncate">{carrierLabel}</div>
                {/* Row 2: Status */}
                <div className={`text-sm font-semibold tabular-nums uppercase tracking-wide leading-snug ${isOverdue ? 'text-red-500' : 'text-blue-600 dark:text-blue-400'}`}>
                    {isOverdue ? `Late ${arrivalRel.replace(' overdue', '')}` : `ETA ${arrivalRel}`}
                </div>
                {/* Row 3: Appointment time */}
                <div className="text-xs font-medium leading-normal text-muted-foreground tabular-nums">Appointment: {formatTimeInTimeZone(appointment.startAt, warehouseTimeZone)}</div>
                {/* Row 4: Load / PO */}
                {loadPoLine && (
                    <div className="text-[11px] font-normal font-mono leading-normal text-muted-foreground truncate">{loadPoLine.label}: {loadPoLine.value}</div>
                )}
                <div className="mt-auto pt-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onCheckIn(); }}
                        className="w-full text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded px-2.5 py-1.5 transition-colors"
                    >
                        Arrived
                    </button>
                </div>
            </div>
        );
    }

    // ── ARRIVED / YARD ──────────────────────────────────────────────
    if (laneKey === 'arrived') {
        const waitRel = relativeTimeLabel(appointment.checkInTime, nowMs, 'elapsed');
        const waitMins = appointment.checkInTime
            ? Math.round((nowMs - new Date(appointment.checkInTime).getTime()) / 60000)
            : 0;
        const sev = waitSeverity(waitMins);
        return (
            <div
                onClick={onClick}
                className={`rounded-lg border transition-all cursor-pointer p-4 flex flex-col gap-1.5 min-h-[120px] group ${sev.borderClass} ${sev.bgClass} hover:border-amber-400/50`}
            >
                {/* Row 1: Carrier */}
                <div className="text-sm font-semibold leading-snug text-foreground truncate">{carrierLabel}</div>
                {/* Row 2: Status */}
                <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold tabular-nums uppercase tracking-wide leading-snug ${sev.textColor}`}>
                        Waiting {waitRel}
                    </span>
                    {sev.label && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400 bg-red-100/60 dark:bg-red-900/30 px-1.5 py-0.5 rounded">{sev.label}</span>
                    )}
                </div>
                {/* Row 3: Assigned dock */}
                <div className={`text-xs font-medium leading-normal ${hasActiveDock && dockLabel ? 'text-purple-600 dark:text-purple-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {isStaleDock 
                        ? 'Dock assignment unavailable \u2014 reassign required' 
                        : `Assigned Dock: ${hasActiveDock && dockLabel ? dockLabel.replace('Dock ', '') : 'Unassigned'}`
                    }
                </div>
                {/* Row 4: Load / PO */}
                {loadPoLine && (
                    <div className="text-[11px] font-normal font-mono leading-normal text-muted-foreground truncate">{loadPoLine.label}: {loadPoLine.value}</div>
                )}
                <div className="mt-auto pt-2">
                    {canDispatch ? (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDispatch(); }}
                            className="w-full text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded px-2.5 py-1.5 transition-colors"
                        >
                            Dispatch to Dock
                        </button>
                    ) : (
                        <button
                            onClick={(e) => { e.stopPropagation(); onClick(); }}
                            className="w-full text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded px-2.5 py-1.5 transition-colors"
                        >
                            Assign Dock
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ── AT DOCK ──────────────────────────────────────────────────────
    if (laneKey === 'atdock') {
        const clockStartMs = (() => {
            const checkInMs = appointment.checkInTime ? new Date(appointment.checkInTime).getTime() : NaN;
            const startAtMs = appointment.startAt ? new Date(appointment.startAt).getTime() : NaN;
            if (isNaN(checkInMs)) return NaN;
            return !isNaN(startAtMs) ? Math.max(checkInMs, startAtMs) : checkInMs;
        })();
        const dwellMins = !isNaN(clockStartMs) ? Math.max(0, Math.round((nowMs - clockStartMs) / 60000)) : 0;
        const detentionStartMs = !isNaN(clockStartMs) ? clockStartMs + detentionGraceMinutes * 60000 : NaN;
        const inFreeTime = !isNaN(detentionStartMs) && nowMs < detentionStartMs;
        const freeTimeRemaining = inFreeTime ? Math.max(0, Math.round((detentionStartMs - nowMs) / 60000)) : 0;
        const detentionElapsed = !inFreeTime && !isNaN(detentionStartMs) ? Math.max(0, Math.round((nowMs - detentionStartMs) / 60000)) : 0;
        const dsev = dockSeverity(dwellMins, detentionGraceMinutes);
        return (
            <div
                onClick={onClick}
                className={`rounded-lg border transition-all cursor-pointer p-4 flex flex-col gap-1.5 min-h-[120px] group hover:border-purple-400/50 dark:hover:border-purple-500/40 ${dsev.borderClass} ${dsev.bgClass}`}
            >
                {/* Row 1: Carrier */}
                <div className="text-sm font-semibold leading-snug text-foreground truncate">{carrierLabel}</div>
                {/* Row 2: Dock identifier */}
                <div className={`text-sm font-semibold uppercase tracking-wide leading-snug ${dsev.textColor}`}>
                    {isStaleDock ? 'Dock unavailable' : (dockLabel || 'Dock: ')}
                </div>
                {/* Row 3: Free time / detention detail */}
                {!isNaN(detentionStartMs) ? (
                    inFreeTime ? (
                        <div className="text-xs tabular-nums font-medium leading-normal text-emerald-600 dark:text-emerald-400">
                            Free time remaining {freeTimeRemaining}m
                        </div>
                    ) : (
                        <div className="text-xs tabular-nums font-medium leading-normal text-red-600 dark:text-red-400">
                            Detention  {detentionElapsed >= 60 ? `${Math.floor(detentionElapsed / 60)}h ${detentionElapsed % 60}m` : `${detentionElapsed}m`}
                        </div>
                    )
                ) : (
                    <div className="text-xs font-medium leading-normal text-muted-foreground"></div>
                )}
                {/* Row 4: Load / PO */}
                {loadPoLine && (
                    <div className="text-[11px] font-normal font-mono leading-normal text-muted-foreground truncate">{loadPoLine.label}: {loadPoLine.value}</div>
                )}
                <div className="mt-auto pt-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onCheckOut(); }}
                        className="w-full text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded px-2.5 py-1.5 transition-colors"
                    >
                        Complete
                    </button>
                </div>
            </div>
        );
    }

    // ── COMPLETED ───────────────────────────────────────────────────
    if (laneKey === 'completed') {
        const completedAgo = appointment.checkOutTime
            ? relativeTimeLabel(appointment.checkOutTime, nowMs, 'elapsed')
            : null;
        const offloadTime = (() => {
            const inMs = appointment.checkInTime ? new Date(appointment.checkInTime).getTime() : NaN;
            const outMs = appointment.checkOutTime ? new Date(appointment.checkOutTime).getTime() : NaN;
            if (isNaN(inMs) || isNaN(outMs) || outMs <= inMs) return null;
            const totalMins = Math.round((outMs - inMs) / 60000);
            if (totalMins >= 60) return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`;
            return `${totalMins}m`;
        })();
        return (
            <div
                onClick={onClick}
                className="rounded-lg border border-border bg-card/80 hover:border-emerald-400/40 transition-all cursor-pointer p-4 flex flex-col gap-1.5 min-h-[120px] opacity-80 hover:opacity-100 group"
            >
                {/* Row 1: Carrier */}
                <div className="text-sm font-semibold leading-snug text-foreground/70 truncate">{carrierLabel}</div>
                {/* Row 2: Status */}
                <div className="text-sm font-semibold tabular-nums uppercase tracking-wide leading-snug text-emerald-600 dark:text-emerald-400">
                    {completedAgo ? `Completed ${completedAgo} ago` : 'Completed'}
                </div>
                {/* Row 3: Offload time */}
                <div className="text-xs font-medium leading-normal text-muted-foreground tabular-nums">
                    Offload time: {offloadTime || ''}
                </div>
                {/* Row 4: Load / PO */}
                {loadPoLine && (
                    <div className="text-[11px] font-normal font-mono leading-normal text-muted-foreground/70 truncate">{loadPoLine.label}: {loadPoLine.value}</div>
                )}
                <div className="mt-auto pt-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onClick(); }}
                        className="w-full text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
                    >
                        View Details
                    </button>
                </div>
            </div>
        );
    }

    // Fallback (should not happen)
    return null;
}


function CarrierDashboard() {
    return <div className="text-center py-12 text-muted-foreground">Carrier Dashboard - Coming Soon</div>;
}

function AdminDashboard() {
    return <div className="text-center py-12 text-muted-foreground">Admin Dashboard - Coming Soon</div>;
}

// Sub-component for Unread Messages Badge (Warehouse Header)
function UnreadMessagesBadge() {
    const { token, user: modalUser } = useAuthStore();
    const isOperationsEntitledModal = isOperationsTierUser(modalUser);
    const [unreadCount, setUnreadCount] = useState(0);
    const [location, setLocation] = useLocation();

    // Use refs to track previous values without triggering effect re-runs
    const prevUnreadCount = useRef(0);
    const lastNotifiedMessageId = useRef<string | null>(null);
    const isFirstLoad = useRef(true);
    const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeToastId = useRef<string | null>(null);

    // CLEAR TOASTS ON ROUTE CHANGE
    useEffect(() => {
        // Dismiss any active message toasts when navigating
        if (activeToastId.current) {
            toast.dismiss(activeToastId.current);
            activeToastId.current = null;
        }

        if (dismissTimeoutRef.current) {
            clearTimeout(dismissTimeoutRef.current);
            dismissTimeoutRef.current = null;
        }

        // Also good practice to clear any generic ones
        toast.dismiss('new-message-toast');
    }, [location]);

    useEffect(() => {
        if (!token) {
            // hard reset
            if (activeToastId.current) {
                toast.dismiss(activeToastId.current);
                activeToastId.current = null;
            }
            toast.dismiss('new-message-toast');

            if (dismissTimeoutRef.current) {
                clearTimeout(dismissTimeoutRef.current);
                dismissTimeoutRef.current = null;
            }

            setUnreadCount(0);
            prevUnreadCount.current = 0;
            lastNotifiedMessageId.current = null;
            isFirstLoad.current = true;
            return;
        }

        const pollUnread = async () => {
            try {
                const data = await safeFetchJson(
                    '/api/messages/unread-count',
                    { headers: { 'Authorization': `Bearer ${token}` } },
                    setLocation
                );

                if (data) {
                    const currentCount = Number(data?.unreadCount ?? 0);
                    const latestMsg = data.latest;

                    // Logic: Only toast if we have a NEW message ID that we haven't seen before
                    // AND it's not the initial load
                    if (!isFirstLoad.current) {
                        const hasNewCount = currentCount > prevUnreadCount.current;
                        const hasNewId = latestMsg?.id && latestMsg.id !== lastNotifiedMessageId.current;

                        if (hasNewCount && hasNewId) {
                            // Determine context for toast
                            const context = latestMsg
                                ? `${latestMsg.carrierName || 'Carrier'}  PO ${latestMsg.customerPoNumber || 'Unknown'}`
                                : 'New Message';

                            // B) Use message-specific ID (or fallback) so new ones replace old ones correctly
                            const tId = latestMsg?.id ? `msg-${latestMsg.id}` : 'new-message-generic';

                            // Dismiss previous if different
                            if (activeToastId.current && activeToastId.current !== tId) {
                                toast.dismiss(activeToastId.current);
                            }
                            activeToastId.current = tId;

                            toast(
                                <div className="flex flex-col gap-1">
                                    <span className="font-bold">New Message Received</span>
                                    <span className="text-xs opacity-90">{context}</span>
                                </div>,
                                {
                                    icon: <Mail className="w-4 h-4" />,
                                    duration: 5000,
                                    id: tId
                                }
                            );

                            // C) Add hard fallback dismiss timer
                            if (dismissTimeoutRef.current) {
                                clearTimeout(dismissTimeoutRef.current);
                            }
                            dismissTimeoutRef.current = setTimeout(() => {
                                toast.dismiss(tId);
                            }, 5500);

                            if (latestMsg?.id) {
                                lastNotifiedMessageId.current = latestMsg.id;
                            }
                        }
                    } else {
                        // Mark first load as done
                        isFirstLoad.current = false;
                        // Seed the last ID so we don't alert on existing unreads
                        if (latestMsg?.id) {
                            lastNotifiedMessageId.current = latestMsg.id;
                        }
                    }

                    // Always update state and ref
                    setUnreadCount(currentCount);
                    prevUnreadCount.current = currentCount;
                }
            } catch (err) {
                // silent fail on poll
            }
        };

        // Initial fetch
        pollUnread();

        // 15s Interval
        const interval = setInterval(pollUnread, 15000);

        // Cleanup: dismiss toast on unmount
        return () => {
            clearInterval(interval);
            if (activeToastId.current) toast.dismiss(activeToastId.current);
            if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
        };
    }, [token]); // Dependency array only includes token

    if (unreadCount === 0) return null;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => setLocation('/inbox')}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setLocation('/inbox');
            }}
            className="relative group cursor-pointer mr-2 outline-none"
            title="Open Inbox"
        >
            <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 transition-colors group-hover:bg-slate-200 dark:group-hover:bg-slate-700">
                <Mail className="w-5 h-5" />
            </div>
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-950 animate-in zoom-in duration-300">
                {unreadCount > 9 ? '9+' : unreadCount}
            </span>
        </div>
    );
}

// Linked POs summary panel for approval requests
function LinkedPOsPanel({ linkedPOs, loadNumber }: { linkedPOs: Appointment['linkedPOs']; loadNumber?: string | null }) {
    const [expanded, setExpanded] = useState(false);
    if (!linkedPOs || linkedPOs.length === 0) return null;

    const displayLimit = 3;
    const showToggle = linkedPOs.length > displayLimit;
    const visible = expanded ? linkedPOs : linkedPOs.slice(0, displayLimit);

    // Exception awareness using shared helpers
    const hasAnyException = linkedPOs.some((po: any) => {
        const ex = extractPOExceptionFromReceiptNotes(po.receiptNotes);
        return !!ex;
    });
    // Single PO: show specific label; Multi-PO: show generic
    let exceptionSummary: string | null = null;
    if (hasAnyException) {
        if (linkedPOs.length === 1) {
            const ex = extractPOExceptionFromReceiptNotes(linkedPOs[0].receiptNotes);
            exceptionSummary = ex ? formatPOException(ex) : 'Exception present';
        } else {
            exceptionSummary = 'Exception present';
        }
    }

    return (
        <div className="rounded-lg border border-cyan-200 dark:border-cyan-800/50 bg-cyan-50/50 dark:bg-cyan-950/20 p-3">
            <h4 className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Package className="w-3 h-3" />
                Linked POs ({linkedPOs.length})
            </h4>
            {loadNumber && (
                <div className="text-[11px] font-mono font-medium text-cyan-600 dark:text-cyan-400 mb-2">
                    Load: {loadNumber}
                </div>
            )}
            <div className="space-y-1">
                {visible.map((po, i) => (
                    <div key={po.poNumber + i} className="flex items-center justify-between text-xs py-1 border-b border-cyan-100 dark:border-cyan-900/30 last:border-0">
                        <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-slate-800 dark:text-slate-200">{po.poNumber}</span>
                            {po.clientName && <span className="text-slate-500 dark:text-slate-400 truncate max-w-[120px]">{po.clientName}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                            {po.expectedPallets != null && (
                                <span className="text-slate-500 dark:text-slate-400 tabular-nums">{po.expectedPallets} plt</span>
                            )}
                            {po.receiptStatus && (
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                    po.receiptStatus === 'complete'
                                        ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                        : po.receiptStatus === 'short'
                                            ? 'bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400'
                                            : po.receiptStatus === 'over'
                                                ? 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400'
                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                                }`}>
                                    {po.receiptStatus}
                                </span>
                            )}
                            {!po.receiptStatus && po.status && (
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                    (po.status || '').toLowerCase() === 'open'
                                        ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                                }`}>
                                    {po.status}
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {showToggle && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="mt-1.5 text-[10px] font-medium text-cyan-600 dark:text-cyan-400 hover:underline"
                >
                    {expanded ? 'Show less' : `+${linkedPOs.length - displayLimit} more POs`}
                </button>
            )}
            {exceptionSummary && (
                <div className="mt-2 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                    ⚠ {exceptionSummary}
                </div>
            )}
        </div>
    );
}

// Request Detail Modal Component (Enhanced with Decline/Revise)
export function RequestDetailModal({
    request,
    onClose,
    onApprove,
    onRefresh,
    warehouseSchedule,
    initialMode = 'view',
    warehouseTimeZone,
}: {
    request: Appointment,
    onClose: () => void,
    onApprove: () => void,
    onRefresh?: () => void,
    warehouseSchedule?: { openTime: string; closeTime: string; slotMinutes: number } | null,
    initialMode?: 'view' | 'decline' | 'revise',
    warehouseTimeZone?: string,
}) {
    const { token, user: modalUser } = useAuthStore();
    const isOperationsEntitledModal = isOperationsTierUser(modalUser);
    const [mode, setMode] = useState<'view' | 'decline' | 'revise'>(initialMode);
    const [loading, setLoading] = useState(false);


    const [, setLocation] = useLocation();

    // Decline State
    const [declineReason, setDeclineReason] = useState('');

    // Revise State
    const [reviseDate, setReviseDate] = useState(request.requestedDate || '');
    const [reviseTime, setReviseTime] = useState(request.requestedTime || '09:00');
    const [reviseUtc, setReviseUtc] = useState<string>('');
    const [reviseNote, setReviseNote] = useState('');

    // Initialize revise state when opening or switching mode
    useEffect(() => {
        if (mode === 'revise') {
            setReviseDate(request.requestedDate || '');
            setReviseTime(request.requestedTime || '09:00');
            setReviseUtc('');
            setReviseNote('');
        }
    }, [mode, request]);

    if (!request) return null;

    const handleDecline = async () => {

        setLoading(true);
        try {
            await safeFetchJson(
                `/api/appointments/${request.id}/decline`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ reason: declineReason })
                },
                setLocation
            );

            toast.success('Request declined');
            onClose();
            if (onRefresh) onRefresh();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRevise = async () => {
        if (!reviseDate || !reviseTime) {
            toast.error('Date and time are required');
            return;
        }

        setLoading(true);
        try {
            await safeFetchJson(
                `/api/appointments/${request.id}/revise-time`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        requestedDate: reviseDate,
                        requestedTime: reviseTime,
                        requestedUtc: reviseUtc,
                        note: reviseNote
                    })
                },
                setLocation
            );

            toast.success('Carrier notified of revised time');
            onClose();
            if (onRefresh) onRefresh();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };




    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1100]" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900/95 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl p-6 max-w-lg w-full m-4 overflow-y-auto max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${mode === 'decline' ? 'bg-red-500' : mode === 'revise' ? 'bg-amber-500' : 'bg-blue-500'}`}></span>
                            {mode === 'view' ? 'Request Details' : mode === 'decline' ? 'Decline Request' : 'Revise Time'}
                        </h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">ID: <span className="font-mono text-slate-600 dark:text-slate-300">{(request?.id || '').slice(0, 8).toUpperCase()}</span></p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors rounded-full p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* VIEW MODE */}
                {mode === 'view' && (
                    <div className="space-y-5">
                        {/* Primary Info */}
                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Requested Date</label>
                                    <div className="font-medium text-slate-900 dark:text-white">
                                        {(() => {
                                            const hasValidDate = (d: any) => !!d && !isNaN(new Date(d).getTime());
                                            if (request.requestedDate) return request.requestedDate;
                                            if (hasValidDate(request.startAt)) {
                                                return formatDateInTimeZone(request.startAt, warehouseTimeZone, false);
                                            }
                                            return '';
                                        })()}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Requested Time</label>
                                    <div className="font-medium text-slate-900 dark:text-white">
                                        {(() => {
                                            const hasValidDate = (d: any) => !!d && !isNaN(new Date(d).getTime());
                                            if (request.requestedTime) return request.requestedTime;
                                            if (hasValidDate(request.startAt)) {
                                                return formatTimeInTimeZone(request.startAt, warehouseTimeZone);
                                            }
                                            return '';
                                        })()}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{request.loadNumber ? 'Load Number' : 'PO Number'}</label>
                                    <div className="font-mono text-cyan-600 dark:text-cyan-400 font-medium">
                                        {request.loadNumber
                                            ? <>{request.loadNumber} <span className="text-slate-400"></span> PO {request.customerPoNumber || ''}</>
                                            : request.customerPoNumber || ''}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Est. Pallets</label>
                                    <div className="font-medium text-slate-900 dark:text-white">{request.estimatedPalletCount ?? ''}</div>
                                </div>
                            </div>
                        </div>

                        {/* Linked POs  visible before approval */}
                        {request.linkedPOs && request.linkedPOs.length > 0 && (
                            <LinkedPOsPanel linkedPOs={request.linkedPOs} loadNumber={request.loadNumber} />
                        )}

                        {/* Risk level badge  deterministic, Operations-tier only */}
                        {isOperationsEntitledModal && request.delayRiskLevel && (
                            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Risk Level:</span>
                                    <RiskBadge
                                        level={request.delayRiskLevel}
                                        score={request.delayRiskScore || 0}
                                        reasons={request.delayRiskReasons || []}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Carrier Info */}
                        <div>
                            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 pb-1">Carrier Information</h3>
                            <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-sm">
                                <div className="col-span-2">
                                    <span className="text-slate-500 block text-xs">Carrier Name</span>
                                    <span className="font-medium text-lg text-slate-900 dark:text-white">{request.carrierName || ''}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs">MC Number</span>
                                    <span className="font-mono text-slate-800 dark:text-white">{request.carrierMcNumber || ''}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs">Driver Name</span>
                                    <span className="text-slate-800 dark:text-white">{request.driverName || ''}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs">Phone</span>
                                    <span className="text-blue-600 dark:text-blue-300 hover:underline">
                                        {request.carrierPhone ? <a href={`tel:${request.carrierPhone}`}>{request.carrierPhone}</a> : ''}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs">Email</span>
                                    <span className="text-blue-600 dark:text-blue-300 hover:underline truncate block">
                                        {request.carrierEmail ? <a href={`mailto:${request.carrierEmail}`}>{request.carrierEmail}</a> : ''}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Equipment Info */}
                        <div>
                            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 pb-1">Equipment Details</h3>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                                <div>
                                    <span className="text-slate-500 block text-xs">Type</span>
                                    <span className="text-slate-800 dark:text-white">{request.trailerType || ''}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs">Truck #</span>
                                    <span className="font-mono text-slate-800 dark:text-white">{request.truckNumber || ''}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs">Trailer #</span>
                                    <span className="font-mono text-slate-800 dark:text-white">{request.trailerNumber || ''}</span>
                                </div>
                            </div>
                        </div>

                        {request.notes && (
                            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded p-3">
                                <label className="text-xs font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wider block mb-1">Notes</label>
                                <p className="text-amber-800 dark:text-amber-100 text-sm italic">"{request.notes}"</p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="mt-2 flex flex-col gap-3">
                            <button
                                onClick={onApprove}
                                className="w-full py-3 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-lg transition-colors shadow-lg shadow-primary/20"
                            >
                                Approve Request
                            </button>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setMode('revise')}
                                    className="flex-1 py-2 px-3 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 text-amber-600 dark:text-amber-500 border border-amber-300 dark:border-amber-500/50 rounded-lg transition-colors text-sm font-medium"
                                >
                                    Revise Time
                                </button>
                                <button
                                    onClick={() => setMode('decline')}
                                    className="flex-1 py-2 px-3 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-500 border border-red-300 dark:border-red-500/50 rounded-lg transition-colors text-sm font-medium"
                                >
                                    Decline
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* DECLINE MODE */}
                {mode === 'decline' && (
                    <div className="space-y-6 animate-in slide-in-from-right duration-200">
                        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-4">
                            <p className="text-red-700 dark:text-red-200 text-sm">
                                You are about to decline this request. The carrier will be notified via email.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Reason for decline (Optional)
                            </label>
                            <textarea
                                value={declineReason}
                                onChange={(e) => setDeclineReason(e.target.value)}
                                className="w-full h-32 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 placeholder-slate-400 dark:placeholder-slate-500 resize-none"
                                placeholder="e.g. Schedule conflict, capacity full..."
                            />
                        </div>
                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={() => setMode('view')}
                                className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDecline}
                                disabled={loading}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors shadow-lg shadow-red-500/20 disabled:opacity-50"
                            >
                                {loading ? 'Declining...' : 'Confirm Decline'}
                            </button>
                        </div>
                    </div>
                )}

                {/* REVISE MODE */}
                {mode === 'revise' && (
                    <div className="space-y-6 animate-in slide-in-from-right duration-200">
                        {/* V1.3A: Late PO Risk Banner */}
                        {(() => {
                            const lateRisk = detectLatePORisk(request);
                            if (!lateRisk) return null;
                            const bgColor = lateRisk.severity === 'critical' ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20'
                                : lateRisk.severity === 'urgent' ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/20'
                                : 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20';
                            const textColor = lateRisk.severity === 'critical' ? 'text-red-700 dark:text-red-300'
                                : lateRisk.severity === 'urgent' ? 'text-orange-700 dark:text-orange-300'
                                : 'text-amber-700 dark:text-amber-300';
                            const iconColor = lateRisk.severity === 'critical' ? 'text-red-500'
                                : lateRisk.severity === 'urgent' ? 'text-orange-500'
                                : 'text-amber-500';
                            return (
                                <div className={`${bgColor} border rounded-lg p-4`}>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className={`w-5 h-5 ${iconColor} mt-0.5 shrink-0`} />
                                        <div>
                                            <p className={`text-sm font-extrabold uppercase tracking-wider ${textColor}`}>{lateRisk.label}</p>
                                            {lateRisk.latePOs.map((po, i) => (
                                                <p key={po.poNumber + i} className={`text-sm ${textColor} mt-1`}>
                                                    PO {po.poNumber} is due {new Date(po.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}.
                                                    {' '}Requested appointment is {formatDateInTimeZone(request.startAt, warehouseTimeZone)}{' '}
                                                    at {formatTimeInTimeZone(request.startAt, warehouseTimeZone)}.
                                                </p>
                                            ))}
                                            <p className={`text-xs ${textColor} opacity-70 mt-1.5`}>Choose a revised time on or before the due date if possible.</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-4">
                            <p className="text-amber-700 dark:text-amber-200 text-sm">
                                Propose a new time for this request. The carrier will be notified and the request will remain pending approval.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">New Date</label>
                                <input
                                    type="date"
                                    required
                                    value={reviseDate}
                                    onChange={(e) => {
                                        setReviseDate(e.target.value);
                                        setReviseTime('');
                                        setReviseUtc('');
                                    }}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                />
                                {/* V1.3A: Due date helper */}
                                {(() => {
                                    const lateRisk = detectLatePORisk(request);
                                    if (!lateRisk) return null;
                                    const earliest = lateRisk.latePOs.reduce((min, po) => {
                                        const d = new Date(po.dueDate);
                                        return d < min ? d : min;
                                    }, new Date(lateRisk.latePOs[0].dueDate));
                                    const color = lateRisk.severity === 'critical' ? 'text-red-500' : lateRisk.severity === 'urgent' ? 'text-orange-500' : 'text-amber-600 dark:text-amber-400';
                                    return (
                                        <p className={`text-xs font-semibold ${color} mt-1.5`}>
                                            Required arrival: {earliest.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                        </p>
                                    );
                                })()}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">New Time</label>
                                <select
                                    value={reviseUtc || (warehouseSchedule && warehouseTimeZone ? generateOperatingSlots(reviseDate || new Date().toISOString().split('T')[0], warehouseTimeZone, warehouseSchedule.openTime || '00:00', warehouseSchedule.closeTime || '23:59', warehouseSchedule.slotMinutes || 30).find(s => s.localTime === reviseTime)?.startUtc || reviseTime : reviseTime)}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (!val) {
                                            setReviseTime('');
                                            setReviseUtc('');
                                            return;
                                        }
                                        const slots = warehouseSchedule && warehouseTimeZone
                                            ? generateOperatingSlots(reviseDate || new Date().toISOString().split('T')[0], warehouseTimeZone, warehouseSchedule.openTime || '00:00', warehouseSchedule.closeTime || '23:59', warehouseSchedule.slotMinutes || 30)
                                            : DEFAULT_REVISE_SLOTS;
                                        const slot = slots.find(s => s.startUtc === val || s.localTime === val);
                                        if (slot) {
                                            setReviseTime(slot.localTime);
                                            setReviseUtc(slot.startUtc);
                                        } else {
                                            setReviseTime('');
                                            setReviseUtc('');
                                        }
                                    }}
                                    className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                                >
                                    <option value="">Select a time...</option>
                                    {(warehouseSchedule && warehouseTimeZone
                                        ? generateOperatingSlots(reviseDate || new Date().toISOString().split('T')[0], warehouseTimeZone, warehouseSchedule.openTime || '00:00', warehouseSchedule.closeTime || '23:59', warehouseSchedule.slotMinutes || 30)
                                        : DEFAULT_REVISE_SLOTS
                                    ).map((slot) => (
                                        <option key={slot.startUtc} value={slot.startUtc}>{slot.displayLabel}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Note to Carrier (Optional)
                            </label>
                            <textarea
                                value={reviseNote}
                                onChange={(e) => setReviseNote(e.target.value)}
                                className="w-full h-24 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-slate-400 dark:placeholder-slate-500 resize-none"
                                placeholder="e.g. Can you do the afternoon slot?"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={() => setMode('view')}
                                className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRevise}
                                disabled={loading}
                                className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-colors shadow-lg shadow-amber-500/20 disabled:opacity-50"
                            >
                                {loading ? 'Sending...' : 'Send Revision'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}







